import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { pluriel } from '@/lib/pluriel';
import { AdminAvatar } from "@/components/admin/avatar";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminCustomerCreateModal } from "@/components/admin/customer-create-modal";
import { AdminDonut } from "@/components/admin/donut";
import { AdminIcon } from "@/components/admin/icon";
import { Kpi } from "@/components/admin/kpi";
import { AdminSpark } from "@/components/admin/spark";
import { PageHead } from "@/components/admin/page-head";
import { Pagination } from "@/components/admin/pagination";
import { Segmented } from "@/components/admin/segmented";
import { Tag } from "@/components/admin/tag";
import { useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import {
	useCustomers,
	useCustomersStats,
	useLoyaltyLiability,
} from "@/lib/customers/customers-hooks";
import { customersApi } from "@/lib/customers/customers-api";
import type { CustomersFilter } from "@/lib/customers/types";
import { fmtFCFA } from "@/lib/format";
import type { TagClass } from "@/lib/status";

const TIER_TO_CLASS: Record<string, TagClass> = {
	platinum: "terra",
	gold: "ochre",
	silver: "info",
	bronze: "neutral",
};
const TIER_LABEL: Record<string, string> = {
	platinum: "Platinum",
	gold: "Gold",
	silver: "Silver",
	bronze: "Bronze",
};
const TIER_COLOR: Record<string, string> = {
	platinum: "#6B4FA1",
	gold: "#D4A24C",
	silver: "#7C8AA0",
	bronze: "#9A6A3A",
};

const SEG_OPTIONS = [
	{ value: "all", label: "Tous" },
	{ value: "vip", label: "VIP" },
	{ value: "new", label: "Nouveau" },
	{ value: "inactive", label: "Inactifs" },
	{ value: "risk", label: "À risque" },
] as const;

type SegValue = (typeof SEG_OPTIONS)[number]["value"];

const PAGE_SIZE = 50;

export function AdminCustomersPage() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const toast = useToast();
	const queryClient = useQueryClient();

	// Modale de création client
	const [showCreateModal, setShowCreateModal] = useState(false);

	useEffect(() => {
		if (searchParams.get("nouveau") === "true" || searchParams.get("nouveau") === "1") {
			setShowCreateModal(true);
			const newParams = new URLSearchParams(searchParams);
			newParams.delete("nouveau");
			setSearchParams(newParams, { replace: true });
		}
	}, [searchParams, setSearchParams]);

	// Filtres
	const [seg, setSeg] = useState<SegValue>("all");
	const [q, setQ] = useState("");
	const [tier, setTier] = useState("");
	const [status, setStatus] = useState("");
	const [city, setCity] = useState("");

	// Pagination (cursor + pile pour « Précédent »)
	const [cursor, setCursor] = useState<string | undefined>(undefined);
	const [, setStack] = useState<string[]>([]);
	const [page, setPage] = useState(1);

	// Export CSV & Merge modal state
	const [isExporting, setIsExporting] = useState(false);
	const [showMergeModal, setShowMergeModal] = useState(false);
	const [mergeSourceId, setMergeSourceId] = useState("");
	const [mergeTargetId, setMergeTargetId] = useState("");
	const [mergeReason, setMergeReason] = useState("");
	const [mergeApprovalId, setMergeApprovalId] = useState("");
	const [isMerging, setIsMerging] = useState(false);

	// Debounce de la recherche libre
	const [debouncedQ, setDebouncedQ] = useState("");
	useEffect(() => {
		const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
		return () => clearTimeout(t);
	}, [q]);

	const baseFilter = useMemo<CustomersFilter>(
		() => ({
			...(debouncedQ ? { q: debouncedQ } : {}),
			...(seg !== "all" ? { segment: seg as CustomersFilter["segment"] } : {}),
			...(tier ? { tier } : {}),
			...(status ? { status } : {}),
			...(city ? { city } : {}),
		}),
		[debouncedQ, seg, tier, status, city],
	);

	// Réinitialise la pagination quand un filtre change
	const filterKey = JSON.stringify(baseFilter);
	useEffect(() => {
		setCursor(undefined);
		setStack([]);
		setPage(1);
	}, [filterKey]);

	const filter: CustomersFilter = { ...baseFilter, limit: PAGE_SIZE, cursor };
	const { data, isLoading, isError } = useCustomers(filter);
	const { data: liability } = useLoyaltyLiability();
	const { data: stats } = useCustomersStats();

	const items = useMemo(() => data?.items ?? [], [data]);
	const accountsByTier = liability?.accountsByTier ?? {
		bronze: 0,
		silver: 0,
		gold: 0,
		platinum: 0,
	};
	const vipAccountsCount = (accountsByTier.gold ?? 0) + (accountsByTier.platinum ?? 0);
	const tierTotalAccounts = Object.values(accountsByTier).reduce((a, b) => a + b, 0);

	const sourceCustomer = useMemo(
		() => items.find((c) => c.id === mergeSourceId),
		[items, mergeSourceId],
	);
	const targetCustomer = useMemo(
		() => items.find((c) => c.id === mergeTargetId),
		[items, mergeTargetId],
	);

	const handleSwapMerge = () => {
		const temp = mergeSourceId;
		setMergeSourceId(mergeTargetId);
		setMergeTargetId(temp);
	};

	const goNext = () => {
		if (!data?.nextCursor) return;
		setStack((s) => [...s, cursor ?? ""]);
		setCursor(data.nextCursor);
		setPage((p) => p + 1);
	};
	const goPrev = () => {
		setStack((s) => {
			const cp = [...s];
			const prev = cp.pop();
			setCursor(prev || undefined);
			return cp;
		});
		setPage((p) => Math.max(1, p - 1));
	};
	const resetFilters = () => {
		setSeg("all");
		setQ("");
		setTier("");
		setStatus("");
		setCity("");
	};

	const handleExport = async (approvalId?: string) => {
		setIsExporting(true);
		try {
			const blob = await customersApi.exportCsv(baseFilter, approvalId);
			const url = window.URL.createObjectURL(
				new Blob([blob], { type: "text/csv;charset=utf-8;" }),
			);
			const a = document.createElement("a");
			a.href = url;
			a.download = `clients-export-${new Date().toISOString().slice(0, 10)}.csv`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
			toast.success("Export CSV téléchargé avec succès.");
		} catch (err: unknown) {
			let message = "Erreur lors de l'export CSV.";
			let is403 = false;
			const ax = err as { response?: { status?: number; data?: unknown } };
			if (ax.response?.data instanceof Blob) {
				try {
					const text = await ax.response.data.text();
					const json = JSON.parse(text);
					if (json.message) message = json.message;
					if (ax.response.status === 403 || message.includes("4-eyes")) is403 = true;
				} catch {
					// retain fallback
				}
			} else if (
				typeof ax.response?.data === "object" &&
				ax.response?.data !== null &&
				"message" in ax.response.data
			) {
				message = String((ax.response.data as { message?: string }).message);
				if (ax.response.status === 403 || message.includes("4-eyes")) is403 = true;
			}

			if (is403) {
				const app = prompt("Double validation requise (≥ 1000 lignes). Saisissez votre approvalId :");
				if (app) {
					await handleExport(app);
					return;
				}
			}
			toast.error(message);
		} finally {
			setIsExporting(false);
		}
	};

	const handleMerge = async () => {
		if (!mergeSourceId || !mergeTargetId || !mergeReason) {
			toast.error("Veuillez sélectionner le client source, le client cible et indiquer un motif.");
			return;
		}
		if (mergeSourceId === mergeTargetId) {
			toast.error("Le client source et le client cible doivent être différents.");
			return;
		}
		setIsMerging(true);
		try {
			await customersApi.merge(mergeSourceId, mergeTargetId, mergeReason, mergeApprovalId || undefined);
			toast.success("Fusion des doublons effectuée avec succès.");
			queryClient.invalidateQueries({ queryKey: ["customers"] });
			setShowMergeModal(false);
			setMergeSourceId("");
			setMergeTargetId("");
			setMergeReason("");
			setMergeApprovalId("");
		} catch (err: unknown) {
			const ax = err as { response?: { data?: { message?: string; approvalId?: string } } };
			const respData = ax.response?.data;
			if (respData?.message === "PENDING_APPROVAL") {
				const appVal = respData.approvalId ?? "";
				if (appVal) setMergeApprovalId(appVal);
				toast.info(
					"Demande d'approbation (4-eyes) enregistrée. Un autre administrateur doit la valider dans Administration > Approbations.",
				);
			} else {
				toast.error(respData?.message ?? "Échec de la fusion des doublons.");
			}
		} finally {
			setIsMerging(false);
		}
	};

	const hasActiveFilters = !!(
		debouncedQ ||
		seg !== "all" ||
		tier ||
		status ||
		city
	);
	const rangeFrom = (page - 1) * PAGE_SIZE + (items.length ? 1 : 0);
	const rangeTo = (page - 1) * PAGE_SIZE + items.length;
	const totalCount = hasActiveFilters
		? items.length
		: (stats?.acquisition?.total ?? items.length);
	const totalPages = data?.hasMore
		? Math.max(page + 1, Math.ceil(totalCount / PAGE_SIZE))
		: Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

	return (
		<>
			<PageHead
				title="Clients"
				sub={
					stats
						? `${stats.acquisition.total.toLocaleString("fr-FR")} clients · activation 30j ${stats.activation.rate30d}%`
						: "Référentiel acheteur Akwaba (CRM)"
				}
				actions={
					<>
						<AdminButton
							variant="ghost"
							disabled={isExporting}
							onClick={() => handleExport()}
						>
							<AdminIcon n="download" s={14} />
							{isExporting ? "Export…" : "Exporter CSV"}
						</AdminButton>
						<AdminButton
							variant="ghost"
							onClick={() => setShowMergeModal(true)}
						>
							<AdminIcon n="users" s={14} />
							Fusionner doublons
						</AdminButton>
						<AdminButton
							variant="primary"
							onClick={() => setShowCreateModal(true)}
						>
							<AdminIcon n="plus" s={14} />
							Nouveau client
						</AdminButton>
					</>
				}
			/>

			<div className="kpi-grid">
				<Kpi
					label="Clients"
					variant="accent"
					icon="users"
					value={(stats?.acquisition.total ?? items.length).toLocaleString("fr-FR")}
					delta="Base totale"
					deltaVariant="accent"
					spark={<AdminSpark data={[12, 14, 15, 18, 22, 25, 28, 32]} color="var(--a-terracotta)" />}
				/>
				<Kpi
					label="VIP (Gold+Platinum)"
					variant="ochre"
					icon="star"
					value={vipAccountsCount.toLocaleString("fr-FR")}
					delta="Comptes premium"
					deltaVariant="warning"
					spark={<AdminSpark data={[2, 3, 3, 5, 6, 7, 8, 10]} color="var(--a-ochre)" />}
				/>
				<Kpi
					label="Activation 7j"
					variant="forest"
					icon="zap"
					value={
						<>
							{stats?.activation.rate7d ?? 0}
							<small>%</small>
						</>
					}
					delta="1ʳᵉ commande"
					deltaVariant="forest"
					spark={<AdminSpark data={[30, 35, 42, 40, 48, 52, 50]} color="var(--a-forest)" />}
				/>
				<Kpi
					label="Passif points"
					variant="warning"
					icon="tag"
					value={(liability?.totalPoints ?? 0).toLocaleString("fr-FR")}
					delta={`${liability?.accounts ?? 0} comptes`}
					deltaVariant="warning"
					spark={<AdminSpark data={[500, 620, 710, 780, 840, 890]} color="var(--a-warning)" />}
				/>
			</div>

			<div className="grid-2">
				<Card>
					<CardHead
						title="Liste clients"
						sub={
							isLoading
								? "Chargement…"
								: items.length
									? `Affichage de ${rangeFrom} à ${rangeTo} client${rangeTo > 1 ? "s" : ""}`
									: "0 client"
						}
						right={
							<Segmented
								value={seg}
								onChange={(v) => setSeg(v as SegValue)}
								options={SEG_OPTIONS.map((o) => ({
									value: o.value,
									label: o.label,
								}))}
							/>
						}
					/>

					{/* Barre de filtres unifiée & compacte */}
					<div className="customer-toolbar">
						<div className="customer-search-wrap">
							<span className="search-icn">
								<AdminIcon n="search" s={14} />
							</span>
							<input
								className="inp-search"
								placeholder="Rechercher nom, téléphone, email…"
								value={q}
								onChange={(e) => setQ(e.target.value)}
								aria-label="Recherche client"
							/>
							{q && (
								<button
									type="button"
									className="clear-btn"
									onClick={() => setQ("")}
									title="Effacer la recherche"
								>
									<AdminIcon n="close" s={12} />
								</button>
							)}
						</div>

						<div className="customer-select-wrap">
							<select
								className={`inp-select ${tier ? "active-filter" : ""}`}
								value={tier}
								onChange={(e) => setTier(e.target.value)}
								aria-label="Filtre palier"
							>
								<option value="">Tous paliers</option>
								<option value="bronze">Bronze</option>
								<option value="silver">Silver</option>
								<option value="gold">Gold</option>
								<option value="platinum">Platinum</option>
							</select>
							<span className="select-icn">
								<AdminIcon n="chevD" s={12} />
							</span>
						</div>

						<div className="customer-select-wrap">
							<select
								className={`inp-select ${status ? "active-filter" : ""}`}
								value={status}
								onChange={(e) => setStatus(e.target.value)}
								aria-label="Filtre statut"
							>
								<option value="">Tous statuts</option>
								<option value="active">Actif</option>
								<option value="suspended">Suspendu</option>
								<option value="anonymized">Anonymisé</option>
							</select>
							<span className="select-icn">
								<AdminIcon n="chevD" s={12} />
							</span>
						</div>

						<div className="customer-city-wrap">
							<span className="city-icn">
								<AdminIcon n="pin" s={12} />
							</span>
							<input
								className={`inp-city ${city ? "active-filter" : ""}`}
								placeholder="Ville"
								value={city}
								onChange={(e) => setCity(e.target.value)}
								aria-label="Filtre ville"
							/>
						</div>

						{hasActiveFilters && (
							<button
								type="button"
								className="filter-reset-btn"
								onClick={resetFilters}
							>
								<AdminIcon n="refresh" s={12} />
								Réinitialiser
							</button>
						)}
					</div>

					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Client</th>
									<th>Statut</th>
									<th>Palier</th>
									<th className="right">Cmds</th>
									<th className="right">LTV</th>
									<th>Créé le</th>
								</tr>
							</thead>
							<tbody>
								{isLoading && (
									<tr>
										<td
											colSpan={6}
											className="muted"
											style={{ textAlign: "center", padding: 24 }}
										>
											Chargement…
										</td>
									</tr>
								)}
								{isError && (
									<tr>
										<td
											colSpan={6}
											className="muted"
											style={{ textAlign: "center", padding: 24 }}
										>
											Erreur de chargement des clients. Réessayez plus tard.
										</td>
									</tr>
								)}
								{!isLoading && !isError && items.length === 0 && (
									<tr>
										<td
											colSpan={6}
											className="muted"
											style={{ textAlign: "center", padding: 24 }}
										>
											Aucun client pour ces critères.
										</td>
									</tr>
								)}
								{items.map((c) => (
									<tr
										key={c.id}
										tabIndex={0}
										style={{ cursor: "pointer" }}
										onClick={() => navigate(`/clients/${c.id}`)}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												navigate(`/clients/${c.id}`);
											}
										}}
									>
										<td>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: 10,
												}}
											>
												<AdminAvatar name={c.name} />
												<div>
													<strong>{c.name}</strong>
													<div className="mono muted" style={{ fontSize: 11 }}>
														{c.phone}
													</div>
												</div>
											</div>
										</td>
										<td>
											<Tag
												variant={
													c.status === "active"
														? "success"
														: c.status === "suspended"
															? "warning"
															: "neutral"
												}
											>
												{c.status}
											</Tag>
										</td>
										<td>
											<Tag variant={TIER_TO_CLASS[c.tier] ?? "neutral"} noDot>
												{TIER_LABEL[c.tier] ?? c.tier}
											</Tag>
										</td>
										<td className="right tn">{c.ordersCount}</td>
										<td className="right tn">
											<strong>{fmtFCFA(c.ltv)}</strong>
										</td>
										<td className="muted" style={{ fontSize: 11 }}>
											{new Date(c.createdAt).toLocaleDateString("fr-FR")}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{/* Pagination */}
					<Pagination
						from={rangeFrom}
						to={rangeTo}
						total={totalCount}
						current={page}
						totalPages={totalPages}
						onChange={(newPage) => {
							if (newPage > page && data?.hasMore) {
								goNext();
							} else if (newPage < page && page > 1) {
								goPrev();
							}
						}}
					/>
				</Card>

				<div className="stack">
					<Card>
						<CardHead
							title="Répartition paliers"
							sub="Effectif de comptes du programme fidélité"
						/>
						<CardBody
							style={{
								display: "flex",
								alignItems: "center",
								gap: 20,
								flexWrap: "wrap",
							}}
						>
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									gap: 8,
								}}
							>
								<AdminDonut
									total={tierTotalAccounts}
									slices={(["platinum", "gold", "silver", "bronze"] as const).map(
										(k) => ({
											color: TIER_COLOR[k],
											value: accountsByTier[k] ?? 0,
										}),
									)}
								/>
								<span className="muted" style={{ fontSize: 11, fontWeight: 600 }}>
									{tierTotalAccounts.toLocaleString("fr-FR")} comptes au total
								</span>
							</div>

							<div className="tier-card-breakdown">
								{(["platinum", "gold", "silver", "bronze"] as const).map((k) => {
									const count = accountsByTier[k] ?? 0;
									const pct =
										tierTotalAccounts > 0
											? Math.round((count / tierTotalAccounts) * 100)
											: 0;
									const isActive = tier === k;
									return (
										<div
											key={k}
											tabIndex={0}
											className={`tier-item-row ${isActive ? "active" : ""}`}
											onClick={() => setTier(isActive ? "" : k)}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault();
													setTier(isActive ? "" : k);
												}
											}}
											title={`Cliquer pour filtrer par le palier ${TIER_LABEL[k]}`}
										>
											<div className="tier-item-head">
												<div className="tier-item-name">
													<span
														className="tier-item-dot"
														style={{ background: TIER_COLOR[k] }}
													/>
													<span>{TIER_LABEL[k]}</span>
												</div>
												<div className="tier-item-stats">
													<span className="tier-item-pct">{pct}%</span>
													<span className="tier-item-count">
														{tierTotalAccounts > 0
															? count.toLocaleString("fr-FR")
															: "—"}
													</span>
												</div>
											</div>
											<div className="tier-item-bar">
												<div
													className="tier-item-bar-fill"
													style={{
														width: `${pct}%`,
														background: TIER_COLOR[k],
													}}
												/>
											</div>
										</div>
									);
								})}
							</div>
						</CardBody>
					</Card>

					<Card>
						<CardHead title="Accès rapides" sub="Raccourcis et outils CRM" />
						<CardBody style={{ padding: 12 }}>
							<div className="quick-access-grid">
								{(
									[
										[
											"Programme fidélité",
											"/clients/fidelite",
											"star",
											"Règles, points & paliers",
											"var(--a-terra-soft)",
											"var(--a-terracotta)",
										],
										[
											"Parrainage",
											"/clients/parrainage",
											"users",
											"Filleuls & commissions",
											"var(--a-ochre-soft)",
											"var(--a-warning)",
										],
										[
											"Demandes RGPD",
											"/clients/rgpd",
											"shield",
											"Exports & anonymisation",
											"var(--a-info-soft)",
											"var(--a-info)",
										],
										[
											"Statistiques",
											"/clients/statistiques",
											"chart",
											"Analyses & rétention",
											"var(--a-forest-soft)",
											"var(--a-forest)",
										],
									] as const
								).map(([label, path, icon, desc, bg, color]) => (
									<button
										key={path}
										type="button"
										className="quick-access-card"
										onClick={() => navigate(path)}
									>
										<div
											className="quick-access-icon"
											style={{ background: bg, color }}
										>
											<AdminIcon n={icon} s={16} />
										</div>
										<div style={{ flex: 1, minWidth: 0 }}>
											<div
												style={{
													fontWeight: 600,
													fontSize: 12.5,
													color: "var(--a-ink)",
												}}
											>
												{label}
											</div>
											<div
												style={{ fontSize: 11, color: "var(--a-ink-mute)" }}
											>
												{desc}
											</div>
										</div>
										<span
											style={{
												color: "var(--a-ink-mute)",
												opacity: 0.6,
												display: "inline-flex",
											}}
										>
											<AdminIcon n="chevR" s={14} />
										</span>
									</button>
								))}
							</div>
						</CardBody>
					</Card>
				</div>
			</div>

			{/* Modale de fusion de doublons */}
			{showMergeModal && (
				<Modal
					title="Fusionner deux doublons clients"
					subtitle="Transférez l'historique et les données d'un compte doublon (source) vers un compte conservé (cible)."
					onClose={() => {
						setShowMergeModal(false);
						setMergeSourceId("");
						setMergeTargetId("");
						setMergeReason("");
						setMergeApprovalId("");
					}}
					width={640}
				>
					<div style={{ display: "grid", gap: 16 }}>
						{/* Sélection source & cible */}
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
							<label style={{ display: "grid", gap: 4 }}>
								<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-terracotta)" }}>
									Client source (doublon supprimé) *
								</span>
								<select
									className="inp"
									value={mergeSourceId}
									onChange={(e) => setMergeSourceId(e.target.value)}
									style={{ borderColor: mergeSourceId ? "var(--a-terracotta)" : undefined }}
								>
									<option value="">-- Choisir client source --</option>
									{items.map((c) => (
										<option key={c.id} value={c.id}>
											{c.name} ({c.phone || c.email || c.id.slice(0, 8)})
										</option>
									))}
								</select>
							</label>

							<label style={{ display: "grid", gap: 4 }}>
								<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-forest)" }}>
									Client cible (compte conservé) *
								</span>
								<select
									className="inp"
									value={mergeTargetId}
									onChange={(e) => setMergeTargetId(e.target.value)}
									style={{ borderColor: mergeTargetId ? "var(--a-forest)" : undefined }}
								>
									<option value="">-- Choisir client cible --</option>
									{items
										.filter((c) => c.id !== mergeSourceId)
										.map((c) => (
											<option key={c.id} value={c.id}>
												{c.name} ({c.phone || c.email || c.id.slice(0, 8)})
											</option>
										))}
								</select>
							</label>
						</div>

						{/* Bouton d'inversion si les 2 sont sélectionnés */}
						{mergeSourceId && mergeTargetId && (
							<div style={{ display: "flex", justifyContent: "center" }}>
								<AdminButton
									type="button"
									variant="ghost"
									size="sm"
									onClick={handleSwapMerge}
									title="Inverser client source et client cible"
									style={{ fontSize: 12 }}
								>
									<AdminIcon n="refresh" s={13} />
									Inverser source et cible
								</AdminButton>
							</div>
						)}

						{/* Carte de comparaison visuelle */}
						{(sourceCustomer || targetCustomer) && (
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "1fr 1fr",
									gap: 12,
									padding: 14,
									background: "var(--a-cream, #f7f4ed)",
									borderRadius: 10,
									border: "1px solid var(--a-border)",
									position: "relative",
								}}
							>
								{/* Card Source */}
								<div
									style={{
										padding: 12,
										borderRadius: 8,
										background: "var(--a-surface)",
										border: "1px solid rgba(198, 86, 58, 0.4)",
										boxShadow: "var(--a-shadow-sm)",
										opacity: sourceCustomer ? 1 : 0.6,
										display: "grid",
										gap: 8,
									}}
								>
									<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
										<Tag variant="danger" noDot>
											DOUBLON SUPPRIMÉ
										</Tag>
										<AdminIcon n="close" s={14} c="var(--a-danger)" />
									</div>
									{sourceCustomer ? (
										<div style={{ display: "grid", gap: 6 }}>
											<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
												<AdminAvatar name={sourceCustomer.name} size="sm" />
												<strong style={{ fontSize: 13 }}>{sourceCustomer.name}</strong>
											</div>
											<div className="muted mono" style={{ fontSize: 11 }}>
												{sourceCustomer.phone || "Sans téléphone"}
											</div>
											<div className="muted" style={{ fontSize: 11 }}>
												{sourceCustomer.email || "Sans e-mail"}
											</div>
											<div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap", fontSize: 11 }}>
												<Tag variant="neutral">{pluriel(sourceCustomer.ordersCount, 'commande')}</Tag>
												<Tag variant="neutral">
													<span className="mono">{fmtFCFA(sourceCustomer.ltv)}</span>
												</Tag>
											</div>
										</div>
									) : (
										<span className="muted" style={{ fontSize: 12 }}>Aucun client source</span>
									)}
								</div>

								{/* Card Cible */}
								<div
									style={{
										padding: 12,
										borderRadius: 8,
										background: "var(--a-surface)",
										border: "1px solid rgba(30, 77, 59, 0.4)",
										boxShadow: "var(--a-shadow-sm)",
										opacity: targetCustomer ? 1 : 0.6,
										display: "grid",
										gap: 8,
									}}
								>
									<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
										<Tag variant="success" noDot>
											COMPTE CONSERVÉ
										</Tag>
										<AdminIcon n="check" s={14} c="var(--a-success)" />
									</div>
									{targetCustomer ? (
										<div style={{ display: "grid", gap: 6 }}>
											<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
												<AdminAvatar name={targetCustomer.name} size="sm" />
												<strong style={{ fontSize: 13 }}>{targetCustomer.name}</strong>
											</div>
											<div className="muted mono" style={{ fontSize: 11 }}>
												{targetCustomer.phone || "Sans téléphone"}
											</div>
											<div className="muted" style={{ fontSize: 11 }}>
												{targetCustomer.email || "Sans e-mail"}
											</div>
											<div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap", fontSize: 11 }}>
												<Tag variant="success">{pluriel(targetCustomer.ordersCount, 'commande')}</Tag>
												<Tag variant="neutral">
													<span className="mono">{fmtFCFA(targetCustomer.ltv)}</span>
												</Tag>
											</div>
										</div>
									) : (
										<span className="muted" style={{ fontSize: 12 }}>Aucun client cible</span>
									)}
								</div>
							</div>
						)}

						{/* Information contacts différents */}
						{sourceCustomer && targetCustomer && (sourceCustomer.phone !== targetCustomer.phone || sourceCustomer.email !== targetCustomer.email) && (
							<div
								style={{
									fontSize: 12,
									lineHeight: 1.45,
									padding: "10px 12px",
									borderRadius: 8,
									background: "var(--a-info-soft, rgba(43,106,142,0.1))",
									color: "var(--a-ink)",
									border: "1px solid rgba(43,106,142,0.25)",
									display: "flex",
									gap: 8,
									alignItems: "flex-start",
								}}
							>
								<span style={{ color: "var(--a-info)", marginTop: 1 }}>
									<AdminIcon n="info" s={15} />
								</span>
								<div>
									<strong>Contacts distincts conservés :</strong> Le numéro (<span className="mono">{sourceCustomer.phone || "aucun"}</span>) et l'e-mail (<strong>{sourceCustomer.email || "aucun"}</strong>) du compte source seront automatiquement ajoutés comme contacts secondaires sur le compte conservé.
								</div>
							</div>
						)}

						{/* Motif de la fusion */}
						<label style={{ display: "grid", gap: 4 }}>
							<span style={{ fontSize: 12, fontWeight: 600 }}>Motif de la fusion *</span>
							<input
								className="inp"
								placeholder="Ex : Doublon créé lors de la commande par téléphone"
								value={mergeReason}
								onChange={(e) => setMergeReason(e.target.value)}
							/>
						</label>

						{/* ID d'approbation 4-eyes */}
						<label style={{ display: "grid", gap: 4 }}>
							<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
								<span style={{ fontSize: 12, fontWeight: 600 }}>ID approbation 4-eyes (optionnel)</span>
								{mergeApprovalId && (
									<Tag variant="warning" noDot>
										4-eyes en attente
									</Tag>
								)}
							</div>
							<div style={{ display: "flex", gap: 8 }}>
								<input
									className="inp mono"
									style={{ flex: 1 }}
									placeholder="ID de validation manager (ex : 1ebc8fee-4bb6-...)"
									value={mergeApprovalId}
									onChange={(e) => setMergeApprovalId(e.target.value)}
								/>
								{mergeApprovalId && (
									<AdminButton
										type="button"
										variant="ghost"
										size="sm"
										onClick={() => {
											navigator.clipboard.writeText(mergeApprovalId);
											toast.success("ID d'approbation copié !");
										}}
										title="Copier l'ID d'approbation"
									>
										Copier
									</AdminButton>
								)}
							</div>
						</label>

						{/* Notice explicative 4-eyes si un ID est présent */}
						{mergeApprovalId && (
							<div
								style={{
									fontSize: 12,
									lineHeight: 1.4,
									padding: "10px 12px",
									borderRadius: 8,
									background: "var(--a-warning-soft, rgba(248,234,200,0.5))",
									color: "var(--a-ink)",
									border: "1px solid var(--a-warning)",
									display: "flex",
									gap: 8,
									alignItems: "flex-start",
								}}
							>
								<span style={{ color: "var(--a-warning)", marginTop: 1 }}>
									<AdminIcon n="shield" s={15} />
								</span>
								<div>
									<strong>Validation 4-eyes requise :</strong> Transmettez cet ID d'approbation à un administrateur/manager pour validation dans <em>Administration &gt; Approbations (4-eyes)</em>. Une fois approuvé, relancez la fusion.
								</div>
							</div>
						)}

						{/* Avertissement de sécurité */}
						<div
							style={{
								fontSize: 12,
								lineHeight: 1.4,
								padding: "10px 12px",
								borderRadius: 8,
								background: "var(--a-ochre-soft, rgba(212,162,76,0.12))",
								color: "var(--a-ink)",
								border: "1px solid var(--a-warning)",
								display: "flex",
								gap: 8,
								alignItems: "flex-start",
							}}
						>
							<span style={{ color: "var(--a-terracotta)", marginTop: 1 }}>
								<AdminIcon n="alert" s={15} />
							</span>
							<div>
								<strong>Attention :</strong> La fusion réaffecte toutes les commandes et données au compte cible. Le compte source sera désactivé. Cette action est irréversible.
							</div>
						</div>

						<ModalFooter>
							<AdminButton
								variant="ghost"
								onClick={() => {
									setShowMergeModal(false);
									setMergeSourceId("");
									setMergeTargetId("");
									setMergeReason("");
									setMergeApprovalId("");
								}}
							>
								Annuler
							</AdminButton>
							<AdminButton
								variant="primary"
								disabled={isMerging || !mergeSourceId || !mergeTargetId || !mergeReason}
								onClick={handleMerge}
							>
								<AdminIcon n="refresh" s={13} />
								{isMerging ? "Fusion en cours…" : "Confirmer la fusion"}
							</AdminButton>
						</ModalFooter>
					</div>
				</Modal>
			)}

			{/* Modale de création d'un nouveau client */}
			<AdminCustomerCreateModal
				isOpen={showCreateModal}
				onClose={() => setShowCreateModal(false)}
			/>
		</>
	);
}
